// src/components/demo/DemoStaffGate.js
//
// Staff gate (SPEC §3.1 / §8.3 screen 1): Google sign-in → role check.
// Unknown users get the request-access form; pending users get a live
// waiting screen (updates the moment a WAKA staffer approves).

import React, { useEffect, useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel, GoogleGlyph, AppleGlyph } from '../foam/FoamPrimitives';
import { FLAVOR_LABEL, IS_DEMO_PREVIEW } from '../../config/flavor';
import { submitStaffRequest, watchOwnStaffRequest } from '../../services/demoService';

const Input = (props) => (
  <input
    {...props}
    className="w-full font-sans text-[16px] px-[14px]"
    style={{
      height: 56, border: '1.5px solid var(--foam-ink)', borderRadius: 0,
      background: '#FFFFFF', color: 'var(--foam-ink)', ...props.style,
    }}
  />
);

export default function DemoStaffGate({ user, role, staffDoc, onSignInGoogle, onSignInApple, onSignOut }) {
  const [name, setName] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [request, setRequest] = useState(undefined); // undefined = loading
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    return watchOwnStaffRequest(user, setRequest);
  }, [user]);

  const header = (
    <FHeader right={IS_DEMO_PREVIEW ? <FLabel color="var(--foam-orange)">{FLAVOR_LABEL}</FLabel> : null} />
  );

  // Signed out — carbon ground, Google + Apple (design handoff §01-1: staff may
  // have no Google account; both providers feed the same request→approve flow).
  if (!user) {
    return (
      <FScreen
        dark
        header={<FHeader dark right={IS_DEMO_PREVIEW
          ? <FLabel color="var(--foam-orange)">{FLAVOR_LABEL}</FLabel>
          : <FLabel color="var(--foam-mute)">Staff</FLabel>} />}
        footer={
          <>
            {/* Paper-filled on carbon (FSignInButtons is light-ground only). Apple
                first on iOS / Google first on Android, same rule as my-waka. */}
            {(/iPad|iPhone|iPod/.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')
              ? ['apple', 'google'] : ['google', 'apple']
            ).map((p) => p === 'apple' ? (
              <FBtn key="apple" dark height={64} onClick={onSignInApple}>
                <AppleGlyph /> Sign in with Apple
              </FBtn>
            ) : (
              <FBtn key="google" dark height={64} onClick={onSignInGoogle}>
                <GoogleGlyph /> Sign in with Google
              </FBtn>
            ))}
            <p className="font-sans text-[12px] text-center" style={{ color: 'var(--foam-mute)' }}>
              Not on the list yet? Sign in and request access — your WAKA contact approves you in seconds.
            </p>
          </>
        }
      >
        <FLabel color="var(--foam-orange)">● Demo Day</FLabel>
        <FHeadline size={64} color="var(--foam-paper)">
          Grab a<br />board.<br /><FHeadline.Accent>Go surf.</FHeadline.Accent>
        </FHeadline>
        <p className="font-sans text-[15px]" style={{ color: '#C4C1B5', maxWidth: 300 }}>
          Staff sign-in for check-out &amp; check-in. Riders never need an account — just their name and a signature.
        </p>
      </FScreen>
    );
  }

  // Revoked / declined
  const declined = request?.status === 'declined' || (staffDoc && staffDoc.active === false);
  if (declined) {
    return (
      <FScreen header={header} footer={<FBtn ghost onClick={onSignOut}>Sign out</FBtn>}>
        <FHeadline size={42}>Access<br /><FHeadline.Accent>not granted.</FHeadline.Accent></FHeadline>
        <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>
          Ask your WAKA contact if you think this is a mistake.
        </p>
      </FScreen>
    );
  }

  // Pending approval
  if (request?.status === 'pending') {
    return (
      <FScreen header={header} footer={<FBtn ghost onClick={onSignOut}>Sign out</FBtn>}>
        <FHeadline size={42}>Waiting for<br /><FHeadline.Accent>approval.</FHeadline.Accent></FHeadline>
        <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>
          Ask your WAKA contact to approve you — this screen updates the moment they do.
        </p>
        <FLabel>{user.email}</FLabel>
      </FScreen>
    );
  }

  // No role, no request yet → request-access form
  const canSubmit = name.trim().length > 1 && affiliation.trim().length > 1 && !submitting;
  return (
    <FScreen
      header={header}
      footer={
        <FBtn
          primary
          disabled={!canSubmit}
          onClick={async () => {
            setSubmitting(true);
            try { await submitStaffRequest(user, { name, affiliation }); } finally { setSubmitting(false); }
          }}
        >
          Request access
        </FBtn>
      }
    >
      <FHeadline size={42}>Request<br /><FHeadline.Accent>access.</FHeadline.Accent></FHeadline>
      <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>
        Signed in as {user.email}. Tell us who you are and a WAKA staffer will approve you on the spot.
      </p>
      <div className="flex flex-col gap-[10px]">
        <FLabel>Your name</FLabel>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        <FLabel>Shop / affiliation</FLabel>
        <Input
          value={affiliation}
          onChange={(e) => setAffiliation(e.target.value)}
          placeholder="e.g. MACkite"
        />
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="font-mono text-[12px] tracking-[0.14em] uppercase underline text-left"
        style={{ color: 'var(--foam-ink-2)', minHeight: 44 }}
      >
        Sign out
      </button>
    </FScreen>
  );
}
